import { useCallback, useEffect, useRef, useState } from 'react';

import { AlertType } from '../../../../common/Alert';
import { openToastAlert } from '../../../../common/ToastAlert/toastAlert';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';

/**
 * Fallback delay (ms) applied when the LaunchDarkly flag can't be read (SDK unavailable
 * or the value isn't a number). We fall back to the safe behavior — no auto-reload, prompt
 * a manual refresh instead — which is equivalent to the flag being 0. The LD-configured
 * default variation is still 2000; this constant only covers the flag-unavailable case.
 */
export const FLAG_UNAVAILABLE_FALLBACK_MS = 0;

export const MANUAL_REFRESH_TOAST_MESSAGE = 'Table not automatically updated';
export const MANUAL_REFRESH_TOAST_DESCRIPTION = 'Refresh the table to see your latest changes.';

export interface UseTraffickingTableAutoReloadResult {
  /**
   * Request a reload of the Trafficking Table after a successful mutation.
   * Reads the reload-delay flag and either schedules a debounced, delayed refetch
   * (flag > 0) or prompts the user to refresh manually (flag === 0).
   */
  scheduleTableReload: () => void;
  /** True while a reload is pending (during the delay) and in flight (during the refetch). */
  isReloading: boolean;
}

/**
 * Encapsulates the post-mutation Trafficking Table reload behavior:
 * read flag -> branch on 0 vs > 0 -> debounce -> setTimeout -> reload -> toggle
 * indicator -> cleanup on unmount.
 *
 * @param reload A function that refetches the relevant table query/queries. It is
 *   only invoked when the flag delay is greater than 0.
 */
export function useTraffickingTableAutoReload(reload: () => Promise<unknown>): UseTraffickingTableAutoReloadResult {
  const { adsMcUiTraffickingTableReloadDelayMs } = useFeatureFlags();

  const delayMs =
    typeof adsMcUiTraffickingTableReloadDelayMs === 'number'
      ? adsMcUiTraffickingTableReloadDelayMs
      : FLAG_UNAVAILABLE_FALLBACK_MS;

  const [isReloading, setIsReloading] = useState(false);

  // Keep the latest delay and reload callback in refs so scheduleTableReload can stay
  // referentially stable (context consumers depend on a stable identity).
  const delayRef = useRef(delayMs);
  delayRef.current = delayMs;

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return (): void => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const scheduleTableReload = useCallback(() => {
    const delay = delayRef.current;

    // Flag disabled: never auto-refetch; prompt the user to refresh manually instead.
    if (delay <= 0) {
      openToastAlert({
        alertType: AlertType.WARNING,
        message: MANUAL_REFRESH_TOAST_MESSAGE,
        description: MANUAL_REFRESH_TOAST_DESCRIPTION,
      });
      return;
    }

    // Debounce: coalesce rapid successive mutations into a single reload by resetting
    // the pending timer, so the refetch fires once after the last mutation settles.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setIsReloading(true);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;

      reloadRef
        .current()
        .catch(() => undefined)
        .finally(() => {
          // Only clear the indicator if no newer reload was scheduled while this one ran.
          if (isMountedRef.current && timerRef.current === null) {
            setIsReloading(false);
          }
        });
    }, delay);
  }, []);

  return { scheduleTableReload, isReloading };
}

export default useTraffickingTableAutoReload;
